import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { McpTokenWithToken } from 'api';
import {
  getGetIntegrationMcpTokensQueryKey,
  usePostIntegrationMcpTokens
} from 'api';
import type { CreateMcpTokenFormValues } from 'validators/mcpToken';
import { createMcpTokenSchema } from 'validators/mcpToken';

import { retryOnlyAfterRefresh } from 'api/mutationRetry';

import { useOrganisationId } from 'hooks/context';

type UseCreateMcpTokenFormArgs = {
  onClose: () => void;
};

export type UseCreateMcpTokenForm = {
  form: UseFormReturn<CreateMcpTokenFormValues>;
  submit: ReturnType<UseFormReturn<CreateMcpTokenFormValues>['handleSubmit']>;
  close: () => void;
  createdToken: McpTokenWithToken | null;
  isPending: boolean;
  isError: boolean;
};

export const useCreateMcpTokenForm = ({
  onClose
}: UseCreateMcpTokenFormArgs): UseCreateMcpTokenForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();

  const mutation = usePostIntegrationMcpTokens({
    mutation: { retry: retryOnlyAfterRefresh, gcTime: 0 }
  });

  const form = useForm<CreateMcpTokenFormValues>({
    mode: 'onChange',
    resolver: yupResolver(
      createMcpTokenSchema
    ) as unknown as Resolver<CreateMcpTokenFormValues>,
    defaultValues: { name: '' }
  });

  // The mutation result is the single raw-token source — the token is never
  // copied into component state or the list query cache.
  const createdToken = mutation.data?.data ?? null;

  // `mutation.isPending` is stale within the submitting render, so a
  // same-tick double submit would pass it — the ref blocks the second POST.
  const inFlightRef = useRef(false);

  const submit = form.handleSubmit((values) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    mutation.mutate(
      { organisationId, data: { name: values.name.trim() } },
      {
        onSettled: () => {
          inFlightRef.current = false;
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetIntegrationMcpTokensQueryKey(organisationId)
          });
        }
      }
    );
  });

  const close = () => {
    if (mutation.isPending) return;
    form.reset();
    mutation.reset();
    onClose();
  };

  const resetMutationRef = useRef(mutation.reset);
  resetMutationRef.current = mutation.reset;
  useEffect(() => () => resetMutationRef.current(), []);

  return {
    form,
    submit,
    close,
    createdToken,
    isPending: mutation.isPending,
    isError: mutation.isError
  };
};
